import { useMemo } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useAttachment from '@hooks/useAttachment'
import useAttachments from '@hooks/useAttachments'
import useCarousel from '@hooks/useCarousel'
import useOrderItem from '@hooks/useOrderItem'
import useUppyAttachmentUpload from '@hooks/useUppyAttachmentUpload'

import type { AttachmentModel, OrderItemModel, ServiceModel } from '@models/types'

const stringifyObjectKeys: (keyof AttachmentModel)[] | undefined = ['data']

const FILE_TYPES = {
  DOCUMENTS: [
    'application/msword',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'video/mp4',
  ],
  IMAGES: [
    'image/*',
  ],
}

type BuildUppyOptionsParams = {
  orderItem: OrderItemModel,
  service: ServiceModel,
}

const buildUppyOptions = (params: BuildUppyOptionsParams) => {
  const { orderItem, service } = params

  const attachmentParams: Partial<AttachmentModel> = {
    attachable_id: orderItem.id,
    attachable_type: 'OrderItem',
    attachment_type: 'deliverable',
  }

  // If the attachment is for a photography or plans service, set the image type
  const shortName = digObject(service, 'short_name', '')

  if (shortName === 'photography' || shortName === 'plans'){
    const imageType = shortName === 'photography' ? 'photo' : 'plan'
    // @ts-expect-error TS2741
    attachmentParams.data = {
      image_type: imageType,
    }
  }

  const stringifiedAttachmentObjectParams = mergeAndStringifyEntityObjects({}, attachmentParams, stringifyObjectKeys)

  const isServiceImageable = digObject(service, 'options.imageable', false)

  const uppyOptions = {
    restrictions: {
      allowedFileTypes: isServiceImageable ? FILE_TYPES.IMAGES : FILE_TYPES.DOCUMENTS,
    },
  }

  return {
    attachmentParams: stringifiedAttachmentObjectParams,
    uppyOptions,
  }
}

type GroupAttachmentType = {
  delivered: Array<AttachmentModel>,
  revised: Array<AttachmentModel>,
}

const groupAttachments = (attachments: AttachmentModel[]) => {
  const grouped: GroupAttachmentType = {
    delivered: [],
    revised: [],
  }

  attachments.forEach((attachment) => {
    const { data } = attachment
    const { revised } = data || {}

    const groupKey = revised ? 'revised' : 'delivered'
    grouped[groupKey].push(attachment)
  })

  return grouped
}

type ProviderCampaignMediaParams = {
  orderItem: OrderItemModel,
}

function useProviderCampaignMedia(params: ProviderCampaignMediaParams) {
  const { orderItem } = params

  const {
    callbacks: {
      manageProviderOrderItemStatusModal,
    },
    service,
    status,
  } = useOrderItem(orderItem)

  const { filteredAttachments, hasFilteredAttachments } = useAttachments({
    filters: {
      attachable_id: orderItem.id,
      attachable_type: 'OrderItem',
      attachment_type: 'deliverable',
    },
  })

  const carouselPayload = useCarousel({ items: filteredAttachments })
  const { callbacks: { setSelectedIndex }, selectedIndex } = carouselPayload

  const attachmentPayload = useAttachment(filteredAttachments[selectedIndex!])
  const {
    attachment: selectedAttachment,
    attachmentType,
  } = attachmentPayload

  const groupedAttachments = useMemo(() => groupAttachments(filteredAttachments), [filteredAttachments])
  const hasDeliveredAttachments = groupedAttachments.delivered.length > 0
  const hasRevisedAttachments = groupedAttachments.revised.length > 0

  // @ts-expect-error TS2739
  const uppyOptions = useMemo(() => buildUppyOptions({ orderItem, service }), [orderItem.id, service.id])
  // @ts-expect-error TS2345
  const uppy = useUppyAttachmentUpload(uppyOptions, [...Object.values(uppyOptions)])

  const hasAdditionalInstructions = !!orderItem.additional_instructions

  return {
    attachmentType,
    callbacks: {
      manageProviderOrderItemStatusModal,
      selectAttachment: (index: number) => {
        setSelectedIndex(index === selectedIndex ? undefined : index)
      },
    },
    carouselPayload,
    groupedAttachments,
    hasAdditionalInstructions,
    hasDeliveredAttachments,
    hasFilteredAttachments,
    hasRevisedAttachments,
    orderItem,
    selectedAttachment,
    selectedAttachmentId: selectedAttachment?.id,
    service,
    status,
    uppy,
  }
}

export type UseProviderCampaignMediaPayload = ReturnType<typeof useProviderCampaignMedia>

export default useProviderCampaignMedia
